import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Typography, Button, Space, Statistic, message, Popconfirm, Modal, Descriptions, Tag } from 'antd';
import { TeamOutlined, PlusOutlined, UploadOutlined, DownloadOutlined, UserOutlined, CheckCircleOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import CoachTable from '../components/shared/CoachTable';
import CoachFilters from './components/CoachFilters';
import CoachForm from './components/CoachForm';
import { filterCoaches, CoachFilters as CoachFiltersType } from './utils/manageCoachUtils';
import { LegacyCoach, Coach } from '../types';
import { apiService } from '../../../services/api';
import { 
  transformCoachToLegacy, 
  transformLegacyToCreateData, 
  transformQueryOptions,
  transformLegacyToUpdateData,
  extractPhoneNumberFromLegacy
} from '../utils/coachDataTransform';

const { Title, Text } = Typography;

const ManageCoaches: React.FC = () => {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState<LegacyCoach[]>([]);
  const [filteredCoaches, setFilteredCoaches] = useState<LegacyCoach[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // Add states for coach details modal
  const [isCoachDetailsModalVisible, setIsCoachDetailsModalVisible] = useState(false);
  const [selectedCoachForDetails, setSelectedCoachForDetails] = useState<LegacyCoach | null>(null);
  const [filters, setFilters] = useState<CoachFiltersType>({
    searchText: '',
    statuses: [],
    specializations: [],
    clientLoadRanges: [],
    onboardingComplete: undefined,
    certifications: [],
    salaryRanges: [],
    performanceRanges: [],
    retentionRanges: [],
  });

  // Load coaches from API
  const loadCoaches = async () => {
    setLoading(true);
    try {
      const queryOptions = transformQueryOptions(filters);
      const response = await apiService.getCoaches(queryOptions);
      
      console.log('API Response:', response); // Debug logging
      
      if (response && response.success && response.data) {
        // Check if response.data.data exists (backend format)
        const coachesData = response.data.data || response.data;
        
        if (Array.isArray(coachesData)) {
          // Transform backend coaches to legacy format for component compatibility
          const legacyCoaches = coachesData.map((coach: any) => 
            transformCoachToLegacy(coach as Coach)
          );
          setCoaches(legacyCoaches);
        } else {
          console.warn('Coaches data is not an array:', coachesData);
          message.error('Invalid data format received from server');
          setCoaches([]);
        }
      } else {
        console.warn('API response unsuccessful or no data:', response);
        
        // If we get here, likely the backend is not running
        if (!response) {
          message.error('Cannot connect to backend. Is the server running on port 8000?');
        } else {
          message.error('Failed to load coaches - server returned error');
        }
        setCoaches([]);
      }
    } catch (error) {
      console.error('Error loading coaches:', error);
      // Check if this is a network error (backend not running)
      if (error instanceof Error) {
        if (error.message.includes('Network Error') || error.message.includes('ECONNREFUSED')) {
          message.error('Cannot connect to server. Please ensure the backend is running.');
        } else {
          message.error(`Error loading coaches: ${error.message}`);
        }
      } else {
        message.error('Error loading coaches. Please try again.');
      }
      setCoaches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoaches();
  }, []);

  useEffect(() => {
    const filtered = filterCoaches(coaches, filters);
    setFilteredCoaches(filtered);
  }, [coaches, filters]);

  const handleFiltersChange = (newFilters: CoachFiltersType) => {
    setFilters(newFilters);
    // Reload data with new filters for backend filtering
    if (newFilters.searchText !== filters.searchText || 
        newFilters.specializations !== filters.specializations ||
        newFilters.statuses !== filters.statuses) {
      setTimeout(() => loadCoaches(), 300); // Debounce API calls
    }
  };

  const handleCreateCoach = async (coachData: Partial<LegacyCoach>) => {
    try {
      setLoading(true);
      
      if (selectedCoachForDetails) {
        // Update existing coach
        console.log('=== COACH UPDATE DEBUG ===');
        console.log('1. Raw form data:', coachData);
        console.log('1a. Form data contains phone?', 'phone' in coachData, 'Value:', coachData.phone);
        
        const updateData = transformLegacyToUpdateData(coachData);
        console.log('2. Transformed coach data (should NOT have phoneNumber):', updateData);
        
        const phoneNumber = extractPhoneNumberFromLegacy(coachData);
        console.log('3. Extracted phone number:', phoneNumber, 'Type:', typeof phoneNumber);
        
        // Combine for API - the API will extract phoneNumber and send separately
        const dataWithPhone = { ...updateData, phoneNumber };
        console.log('4. Final data being sent to API (phoneNumber will be extracted):', dataWithPhone);
        
        const response = await apiService.updateCoach(selectedCoachForDetails.id, dataWithPhone);
        
        console.log('5. API Response:', response);
        
        if (response.success && response.data) {
          message.success('Coach updated successfully');
          setIsModalVisible(false);
          setSelectedCoachForDetails(null);
          await loadCoaches();
        } else {
          message.error(response.error || 'Failed to update coach');
        }
      } else {
        // Create new coach
        const createData = transformLegacyToCreateData(coachData);
        const response = await apiService.createCoach(createData);
        
        if (response.success && response.data) {
          message.success('Coach created successfully');
    setIsModalVisible(false);
          await loadCoaches();
        } else {
          message.error(response.error || 'Failed to create coach');
        }
      }
    } catch (error) {
      console.error('Error saving coach:', error);
      message.error('Error saving coach. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCoach = (coach: LegacyCoach) => {
    setSelectedCoachForDetails(coach);
    setIsCoachDetailsModalVisible(true);
  };

  const handleEditCoach = (id: string, coach: LegacyCoach) => {
    // Open edit modal with coach data
    setSelectedCoachForDetails(coach);
    setIsModalVisible(true);
  };

  // Handle bulk actions for selected coaches
  const handleBulkEdit = () => {
    if (selectedRowKeys.length === 1) {
      const coach = coaches.find(c => c.id === selectedRowKeys[0]);
      if (coach) {
        handleEditCoach(coach.id, coach);
      }
    } else {
      message.info('Please select only one coach to edit');
    }
  };

  const handleBulkView = () => {
    if (selectedRowKeys.length === 1) {
      const coach = coaches.find(c => c.id === selectedRowKeys[0]);
      if (coach) handleViewCoach(coach);
    } else {
      message.info('Please select only one coach to view details');
    }
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      for (const coachId of selectedRowKeys) {
        // Fix the API call - don't send body data for PATCH deactivate
        await apiService.deactivateCoach(coachId as string);
      }
      message.success(`${selectedRowKeys.length} coaches deactivated successfully`);
      setSelectedRowKeys([]);
      await loadCoaches();
    } catch (error) {
      console.error('Error deactivating coaches:', error);
      message.error('Error deactivating coaches. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handleDeleteCoach = async (id: string) => {
    try {
      setLoading(true);
      // Deactivate coach instead of deleting
      const response = await apiService.deactivateCoach(id);
      
      if (response.success) {
        message.success(response.data?.message || 'Coach deactivated successfully');
        await loadCoaches();
      } else {
        message.error(response.error || 'Failed to deactivate coach');
      }
    } catch (error) {
      console.error('Error deactivating coach:', error);
      message.error('Error deactivating coach. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // For now, export current filtered data as CSV
      const csvContent = "data:text/csv;charset=utf-8," + 
        "Name,Email,Phone,Status,Assigned Clients,Specializations\n" +
        filteredCoaches.map(coach => 
          `"${coach.firstName} ${coach.lastName}","${coach.email}","${coach.phone}","${coach.status}",${coach.assignedClients},"${coach.specializations.join(';')}"`
        ).join("\n");
      
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `coaches_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('Coaches data exported successfully');
    } catch (error) {
      message.error('Error exporting data');
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2}><TeamOutlined /> Manage Coaches</Title>
          <Text type="secondary">Coach lifecycle management and performance tracking</Text>
        </div>
        <Space>
          <Button icon={<UploadOutlined />}>Import</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setIsModalVisible(true)}
            loading={loading}
          >
            New Coach
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Coaches"
              value={filteredCoaches.length}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Coaches"
              value={filteredCoaches.filter(c => c.status === 'Active').length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Clients Assigned"
              value={filteredCoaches.reduce((sum, coach) => sum + coach.assignedClients, 0)}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Performance Rating"
              value={filteredCoaches.filter(c => c.performanceRating > 0).reduce((sum, coach) => sum + coach.performanceRating, 0) / filteredCoaches.filter(c => c.performanceRating > 0).length || 0}
              precision={1}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>



      {/* Filters */}
      <CoachFilters 
        filters={filters} 
        onFiltersChange={handleFiltersChange}
        filteredCount={filteredCoaches.length}
      />

      {/* Main Table */}
      <Card style={{ marginTop: 16 }}>
        <CoachTable
          coaches={filteredCoaches}
          loading={loading}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={setSelectedRowKeys}
          onEdit={handleEditCoach}
          onDelete={handleDeleteCoach}
          onView={handleViewCoach}
        />
      </Card>

      {/* Create Coach Modal */}
      <CoachForm
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedCoachForDetails(null);
        }}
        onSubmit={handleCreateCoach}
        initialValues={selectedCoachForDetails || undefined}
      />

      {/* Coach Details Modal */}
      <Modal
        title="Coach Details"
        open={isCoachDetailsModalVisible}
        onCancel={() => {
          setIsCoachDetailsModalVisible(false);
          setSelectedCoachForDetails(null);
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => setIsCoachDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              if (selectedCoachForDetails) {
                handleEditCoach(selectedCoachForDetails.id, selectedCoachForDetails);
                setIsCoachDetailsModalVisible(false);
              }
            }}
          >
            Edit Coach
          </Button>,
        ]}
      >
        {selectedCoachForDetails && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Name">
              {selectedCoachForDetails.firstName} {selectedCoachForDetails.lastName}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedCoachForDetails.email}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {selectedCoachForDetails.phone || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedCoachForDetails.status === 'Active' ? 'green' : selectedCoachForDetails.status === 'Onboarding' ? 'blue' : 'red'}>
                {selectedCoachForDetails.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Assigned Clients">
              {selectedCoachForDetails.assignedClients}
            </Descriptions.Item>
            <Descriptions.Item label="Performance Rating">
              {selectedCoachForDetails.performanceRating > 0 ? `${selectedCoachForDetails.performanceRating.toFixed(1)}/5` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Client Satisfaction">
              {selectedCoachForDetails.clientSatisfactionScore > 0 ? `${selectedCoachForDetails.clientSatisfactionScore.toFixed(1)}/5` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Retention Rate">
              {selectedCoachForDetails.retentionRate > 0 ? `${selectedCoachForDetails.retentionRate.toFixed(1)}%` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {selectedCoachForDetails.startDate || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Salary">
              {selectedCoachForDetails.salary ? `$${selectedCoachForDetails.salary}` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Specializations" span={2}>
              <Space>
                {selectedCoachForDetails.specializations?.map((spec: string, index: number) => (
                  <Tag key={index} color="blue">{spec}</Tag>
                )) || 'None'}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Certifications" span={2}>
              <Space>
                {selectedCoachForDetails.certifications?.map((cert: string, index: number) => (
                  <Tag key={index} color="green">{cert}</Tag>
                )) || 'None'}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {selectedCoachForDetails.notes || 'No notes'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Bulk Actions Bar - Show when coaches are selected */}
      {selectedRowKeys.length > 0 && (
        <Card
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <Space>
            <Text strong>{selectedRowKeys.length} selected</Text>
            <Button size="small" icon={<EyeOutlined />} onClick={handleBulkView}>
              View Details
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={handleBulkEdit}>
              Edit Coach
            </Button>
            <Button size="small" icon={<UserOutlined />}>
              Assign Client
            </Button>
            <Popconfirm
              title={`Are you sure you want to deactivate ${selectedRowKeys.length} coach(es)?`}
              onConfirm={handleBulkDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" icon={<DeleteOutlined />} danger>
                Deactivate
              </Button>
            </Popconfirm>
            <Button
              size="small"
              onClick={() => setSelectedRowKeys([])}
            >
              Clear Selection
            </Button>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default ManageCoaches; 